import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { EditGroupComposer } from "@/forms/rema/editGroup/composer"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Users, Loader2 } from "lucide-react"
import { useToast } from "@/contexts/ToastContext"

function EditGroupPage() {
  const { groupNumber } = Route.useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  // reMa has no getGroupByNumber endpoint and getGroups returns the entire
  // table, so the record is found client-side (mirrors the role edit route).
  const { data, isLoading } = useApiQuery("getGroups", {})
  const group = ((data as any)?.content?.groups || (data as any)?.groups || []).find(
    (g: any) => String(g.groupNumber) === String(groupNumber)
  )

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Group</CardTitle>
              <CardDescription className="text-base">Update group configuration</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
      <Card className="shadow-sm">
        <CardContent className="p-6">
          <EditGroupComposer
            showHeader={false}
            initialValues={{
              groupNumber: group?.groupNumber,
              namespace: group?.namespace as any,
              organization: group?.organization ?? '',
              groupName: group?.groupName ?? '',
              groupDescription: group?.groupDescription ?? '',
              roles: group?.roles ?? [],
              members: group?.members ?? [],
            }}
            submitLabel="Save Changes"
            onSuccess={() => navigate({ to: '/rema/groups' })}
            onError={(error) => toast({ title: 'Error', description: `Failed to update group: ${error.message}`, type: 'error' })}
            onCancel={() => navigate({ to: '/rema/groups' })}
          />
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute("/rema/groups/$groupNumber_/edit")({
  staticData: {
    title: 'Edit Group',
    isSecured: true,
  },
  component: EditGroupPage,
})
